import React, { FC, useEffect, useState } from 'react'
import Joi from 'joi';
import Button from '@mui/joy/Button';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import Skeleton from 'react-loading-skeleton';
import NewAccModal from '../Users/NewAccModal';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ToastMessage from '../../../common/ToastMessage';
import FormControlLabel from '@mui/material/FormControlLabel';
import useRoles from '../../../store/ReactQueries/users/useRoles';
import useAddUsers from '../../../store/ReactQueries/users/useAddUsers';
import useUpdateUser from '../../../store/ReactQueries/users/useUpdateUser';
import useUserByEmail from '../../../store/ReactQueries/users/useUserByEmail';
import { EndPoints } from '../../../utils/apiEndpoints';
import { useNavigate, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useUserStore } from '../../../store/useUserStore';
import { FieldErrors } from '../../../domain/helpers/Errors';
import { UserInterface } from '../../../models/UserModel/User';
import { validateSchema } from '../../../common/ValidateSchema';
import { generateNewPassword } from '../../Auth/GeneratePassword';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { darkModeBackgroudInputs } from '../../../utils/constants';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { userSchema } from '../../../domain/ValidationSchemas/auth/user';

interface AddUserProps {
    mode: "ADD" | "EDIT" | "VIEW" | "PROFILE";
}
export const AddAnUser: FC<AddUserProps> = ({ mode }) => {

    const add = mode === 'ADD';
    const view = mode === 'VIEW';
    const profile = mode === 'PROFILE';

    const navigate = useNavigate();
    const { user } = useUserStore();
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: roles, error: rolesError } = useRoles();
    const { email: emailById } = useParams<{ email: string }>();

    const email = emailById ? emailById : `${user ? user.email : ''}`;

    const addNewUser = useAddUsers(0, 0);
    const updateUser = useUpdateUser(1, 20);
    const { data: userById, error: userByIdError } = useUserByEmail(add ? '' : email);

    const initialState: UserInterface = {
        id: '',
        firstname: '',
        lastname: '',
        email: '',
        roles: [],
        isActive: true,
    };

    const [modal, setModal] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [form, setForm] = useState<UserInterface>(initialState);
    const [isBtnSuccess, setIsBtnSuccess] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (userByIdError) {
            ToastMessage.Failed(`Error: ${userByIdError.message}`);
            navigate(-1);
        }
        if (rolesError) {
            ToastMessage.Failed(`Error: ${rolesError.message}`);
            navigate(-1);
        }
    }, [userByIdError, rolesError]);

    useEffect(() => {
        if (userById) {
            let result = userById as UserInterface;
            setForm(result);
        } else setForm(initialState);
    }, [emailById, userById]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value, id } = e.target;
        setForm({ ...form, [id]: value });
        delete errors?.[id];
    };

    const handleSuccess = (resolve: (value: void | PromiseLike<void>) => void) => {
        setModal(true);
        ToastMessage.Success();
        resolve();
    };

    const handleError = (error: unknown, reject: (reason?: any) => void) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();

        reject(error);
    };

    let items: string[] = ['firstname', 'lastname', 'email', 'isActive'];

    if (userById !== undefined && !profile) items.splice(3, 0, "roles");
    if (profile) items = items.filter(item => item !== 'isActive');

    const fieldTextCss = {
        color: darkMode ? 'white' : 'black',
        '& .MuiInputLabel-root': {
            color: darkMode ? 'white' : 'black',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: darkMode ? 'white' : 'black',
            },
            '&:hover fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '&.Mui-focused fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '& input': {
                color: darkMode ? 'white' : 'black',
            },
        },
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsBtnSuccess(false);
        let isValid;

        if (form.id) {
            const upUserSchema = userSchema.append({
                roles: Joi.array()
                    .items(Joi.string())
                    .min(1)
                    .required()
                    .label("Roles"),
            });

            isValid = validateSchema(upUserSchema, form);
        } else isValid = validateSchema(userSchema, form);

        setErrors(isValid.errors);

        if (!isValid.result) return;
        try {
            await new Promise<void>((resolve, reject) => {
                const { id, firstname, lastname, email, roles, isActive } = form;
                const password = generateNewPassword();
                setPassword(password);

                let upForm: UserInterface = {
                    id,
                    email,
                    roles,
                    lastname,
                    password,
                    isActive,
                    firstname,
                }

                if (upForm.id === '') {
                    const { id, roles, isActive, ...formData } = upForm;
                    addNewUser.mutate(formData, {
                        onSuccess: () => handleSuccess(resolve),
                        onError: (error) => handleError(error, reject),
                    });
                }
                else {
                    updateUser.mutate(upForm, {
                        onSuccess: () => handleSuccess(resolve),
                        onError: (error: unknown) => handleError(error, reject),
                    });
                }
                setIsBtnSuccess(true);
                if (id === '') {
                    const handleCopy = () => {
                        ToastMessage.Info('Text copied to clipboard!');
                        ToastMessage.Success();
                        navigate(EndPoints.USERS);
                    }
                    return (
                        <CopyToClipboard text={password} onCopy={handleCopy}>
                            <button>Copy Password</button>
                        </CopyToClipboard>
                    );
                } else {
                    ToastMessage.Success();
                    navigate(EndPoints.USERS);
                }
            });
        } catch (ex: unknown) {
            if (ex instanceof Error) {
                console.log(ex.message);
            } else {
                console.log("An unknown error occurred");
            }
        }

    };

    const handleRoles = (e: SelectChangeEvent<string>) => {
        const value = e.target.value;
        delete errors?.['roles'];
        setForm({ ...form, roles: value ? [value] : [] })
    }

    const handleReadOnly = (item: string) => {
        if (view) return true
        else if (item === 'email' && !add) return true;
        else return false;
    }

    return <React.Fragment>
        <div>
            <form onSubmit={handleSubmit}>
                <div className='grid-inputs-2'>
                    {
                        loading
                            ? <>
                                {[1, 2, 3, 4].map(item => <Skeleton key={`${item}-loading-inputs`} style={{ width: '100%', height: 56 }} />)}
                            </>
                            : items.map((item, index) => {
                                return item === 'isActive'
                                    ? <div
                                        key={`user-inputs-${index}}`}
                                        className={`d-flex ms-2 mt-2 ${!form.id && 'd-none'}`}
                                    >
                                        <FormGroup>
                                            <FormControlLabel
                                                className={`${darkMode && 'text-white'}`}
                                                control={
                                                    <Checkbox
                                                        id='isActive'
                                                        checked={Boolean(form['isActive']) ?? false}
                                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                                        disabled={view}
                                                        sx={{
                                                            color: view ? 'white' : 'inherit',
                                                            '&.Mui-disabled': { color: 'grey' }
                                                        }}
                                                    />}
                                                label={language['IsActiveUser'] || 'isActive'}
                                                sx={{ '& .MuiFormControlLabel-label.Mui-disabled': { color: darkMode ? 'white' : 'initial' } }}
                                            />
                                        </FormGroup>
                                    </div>
                                    : item === 'roles'
                                        ? <FormControl key={`user-inputs-${index}}`} fullWidth className='mt-2'>
                                            <InputLabel
                                                id="roles-select-component"
                                                className={`${darkMode && 'text-white'}`}
                                            >
                                                {language[item]}
                                            </InputLabel>
                                            <Select
                                                labelId="roles-select-component"
                                                id="roles-select-component"
                                                value={form && form.roles && form.roles.length > 0 ? form.roles[0] : ''}
                                                label={language[item]}
                                                onChange={handleRoles}
                                                disabled={view}
                                                sx={{
                                                    color: darkMode ? 'white' : 'inherit',
                                                    borderColor: darkMode ? 'white' : 'inherit',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'white' : 'inherit',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? 'white' : 'inherit',
                                                    },
                                                    '.MuiSvgIcon-root': {
                                                        color: darkMode ? 'white' : 'inherit',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: darkMode ? 'white !important' : 'inherit',
                                                        '.MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'white' : 'inherit',
                                                        },
                                                    },
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            color: darkMode ? 'white' : 'black',
                                                            backgroundColor: darkMode ? darkModeBackgroudInputs : 'white',
                                                        },
                                                    },
                                                }}
                                            >
                                                {roles?.map(role =>
                                                    <MenuItem value={role} key={role}>{role}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                        : <TextField
                                            key={`user-inputs-${index}}`}
                                            id={item}
                                            sx={fieldTextCss}
                                            variant="outlined"
                                            value={form[item]}
                                            className='mt-2 mb-2'
                                            helperText={errors?.[item]}
                                            error={Boolean(errors?.[item])}
                                            onChange={(e) => handleChange(e)}
                                            InputProps={{ readOnly: handleReadOnly(item) }}
                                            label={`${item[0].toUpperCase()}${item.slice(1)}`}
                                            InputLabelProps={{ style: { color: darkMode ? 'white' : 'black' } }}
                                        />
                            }
                            )
                    }
                </div>
                <div className='d-flex w-100 justify-content-end p-4'>
                    <div className={`d-flex align-items-center w-50 gap-3 ${view && 'justify-content-end'}`}>
                        <Button
                            className='w-50'
                            sx={{
                                fontSize: '16px',
                                background: darkMode ? darkModeBackgroudInputs : "white",
                                border: `1px solid var(--btn-color)`,
                                color: darkMode ? 'white' : "var(--btn-color)",
                                '&:hover': {
                                    background: darkMode ? darkModeBackgroudInputs : "white",
                                    opacity: .7,
                                }
                            }}
                            onClick={() => navigate(EndPoints.USERS)}
                        >
                            {language['Cancel']}
                        </Button>
                        {
                            !view
                            && <Button
                                type='submit'
                                className="w-50"
                                disabled={isBtnSuccess}
                                sx={{
                                    color: "white",
                                    fontSize: '16px',
                                    background: "var(--btn-color)",
                                    '&:hover': {
                                        background: "var(--btn-color)",
                                        opacity: .7
                                    }
                                }}
                            >
                                {language['Save']}
                            </Button>
                        }
                    </div>
                </div>
            </form>
        </div>
        {
            modal
            && form.id === ''
            && <NewAccModal
                open={modal}
                setOpen={setModal}
                email={form.email}
                password={password}
                route={EndPoints.USERS}
            />
        }
    </React.Fragment>
}
export default AddAnUser